/**
 * The report OPTIMA is preparing, under its answer (bata-platform ADR 2137, ADR 9071).
 *
 * **Drawn from the structured fields, never from the model's prose.** What the person reads here is
 * what the platform's digest covers and what «Sì, invia la segnalazione» files: the kind, the
 * title, the requirement, the example, the screens (by their navigation names), the markets, the
 * runs, the figures expected, the acceptance criteria and the steps — each under a catalogue label,
 * each value as text (ADR 9060, 9080: the same «no markup path» rule as an answer). A field not
 * given says so in words.
 *
 * Below it: «Mancano ancora …», the platform's list of what the report still needs, in the same
 * words as the form's fields; and, only when the platform returned a confirmation, the two ways to
 * file — «Sì, invia la segnalazione», which sends the single-use token, and «Apri nel modulo», the
 * report form pre-filled for the person to edit. A typed «sì» files nothing: the panel never reads
 * a question's text.
 */

import { FileText, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useProblemMessage } from '../../api';
import { Button } from '../../primitives';
import { fieldWords } from '../feedback/rubric';
import { kindWords, useSectionLabel } from '../feedback/words';

import type { IntakeDraft, TurnIntake } from './api';
import { isDraftSuperseded, plainText, reportKindOf, type Filing } from './optima';

export interface DraftCardProps {
  readonly intake: TurnIntake & { readonly draft: IntakeDraft };
  readonly filing: Filing | undefined;
  readonly onFile: () => void;
  readonly onOpenInForm?: () => void;
  readonly onOpenMyReports?: () => void;
}

function Row({ labelKey, children }: { readonly labelKey: string; readonly children: React.ReactNode }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-micro text-ink-secondary uppercase">{t(labelKey)}</dt>
      <dd className="text-body text-ink-primary whitespace-pre-wrap break-words">{children}</dd>
    </div>
  );
}

export function DraftCard({ intake, filing, onFile, onOpenInForm, onOpenMyReports }: DraftCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const sectionLabel = useSectionLabel();
  const problemText = useProblemMessage();
  const { draft, missing, confirmation } = intake;
  const tooLong = intake.too_long ?? [];
  const kind = reportKindOf(draft.kind);

  const none = <span className="text-ink-secondary">{t('optima.intake.notGiven')}</span>;
  const textOf = (value: string): React.ReactNode => (value.trim() === '' ? none : plainText(value));
  const listOf = (values: readonly string[], words: (value: string) => string = (value) => value): React.ReactNode => {
    const kept = values.filter((value) => value.trim() !== '');
    return kept.length === 0 ? none : kept.map((value) => plainText(words(value))).join(', ');
  };
  const criteria = draft.acceptance_criteria.filter((value) => value.trim() !== '');
  const filed = filing?.state === 'filed' ? filing.receipt : null;
  // A used, expired or superseded token cannot file: the person revises the draft with OPTIMA, or opens it in the form.
  const superseded = filing?.state === 'failed' && isDraftSuperseded(filing.error);

  return (
    <section aria-label={t('optima.intake.card')} data-optima-draft="" className="border-hairline flex flex-col gap-3 rounded-control border p-3">
      <h3 className="text-label text-ink-primary flex items-center gap-2 font-semibold">
        <FileText size={14} aria-hidden />
        {t('optima.intake.title')}
      </h3>
      <dl className="flex flex-col gap-2">
        <Row labelKey="feedback.reports.col.kind">{kind === null ? plainText(draft.kind) : kindWords(t, kind)}</Row>
        <Row labelKey="feedback.field.title">{textOf(draft.title)}</Row>
        <Row labelKey="feedback.field.requirement">{textOf(draft.requirement)}</Row>
        <Row labelKey="feedback.field.examples">{textOf(draft.examples)}</Row>
        <Row labelKey="feedback.field.screens">{listOf(draft.screens, sectionLabel)}</Row>
        <Row labelKey="feedback.field.markets">{listOf(draft.markets)}</Row>
        <Row labelKey="feedback.field.runs">{listOf(draft.runs)}</Row>
        <Row labelKey="feedback.field.expectedFigures">
          {draft.expected_figures.trim() === '' && draft.expected_figures_not_applicable_reason.trim() !== ''
            ? t('optima.intake.notApplicable', { reason: plainText(draft.expected_figures_not_applicable_reason) })
            : textOf(draft.expected_figures)}
        </Row>
        <Row labelKey="feedback.field.acceptanceCriteria">
          {criteria.length === 0 ? none : (
            <ol className="list-decimal ps-4">
              {criteria.map((criterion, index) => <li key={index}>{plainText(criterion)}</li>)}
            </ol>
          )}
        </Row>
        {draft.steps.trim() !== '' && <Row labelKey="feedback.field.steps">{plainText(draft.steps)}</Row>}
      </dl>

      {missing.length > 0 && (
        <div className="flex flex-col gap-1" data-optima-missing="">
          <p className="text-caption text-ink-primary font-semibold">{t('optima.intake.missing')}</p>
          <ul className="text-caption text-ink-primary list-disc ps-4">
            {missing.map((code) => <li key={code}>{fieldWords(t, code)}</li>)}
          </ul>
        </div>
      )}

      {tooLong.length > 0 && (
        <div className="flex flex-col gap-1" data-optima-too-long="">
          <p className="text-caption text-ink-primary font-semibold">{t('optima.intake.tooLong')}</p>
          <ul className="text-caption text-ink-primary list-disc ps-4">
            {tooLong.map((code) => <li key={code}>{fieldWords(t, code)}</li>)}
          </ul>
        </div>
      )}

      {filed !== null && (
        <div role="status" className="flex flex-col items-start gap-1" data-optima-filed="">
          <p className="text-body text-ink-primary font-semibold">{t('optima.intake.filed', { code: filed.code })}</p>
          {onOpenMyReports !== undefined && (
            <Button variant="secondary" size="sm" onClick={onOpenMyReports}>{t('nav.myReports')}</Button>
          )}
        </div>
      )}

      {filed === null && confirmation !== null && (
        <div className="flex flex-col gap-2">
          <p className="text-caption text-ink-secondary">{t('optima.intake.ready')}</p>
          <p className="text-caption text-ink-secondary" data-optima-provider-notice="">{t('optima.intake.providerNotice')}</p>
          {filing?.state === 'failed' && (
            <div role="alert" className="flex flex-col gap-1">
              <p className="text-body text-status-critical font-semibold">{t('optima.intake.failed')}</p>
              <p className="text-caption text-ink-secondary">
                {superseded ? t('optima.intake.superseded') : problemText(filing.error)}
              </p>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {!superseded && (
              <Button
                variant="primary"
                size="sm"
                icon={<Send size={12} aria-hidden />}
                loading={filing?.state === 'sending'}
                disabled={filing?.state === 'sending'}
                onClick={onFile}
              >
                {t('optima.intake.send')}
              </Button>
            )}
            {onOpenInForm !== undefined && (
              <Button variant="secondary" size="sm" onClick={onOpenInForm}>{t('optima.intake.openInForm')}</Button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
